import { LotacaoEditableTableModel } from 'components/form/editable-table/LotacaoEditableTable'
import { Cidadao } from 'graphql/types.generated'
import { calcularIMC } from 'util/atendimento'
import { toDate } from 'util/date/formatDate'
import { formatNumber } from 'util/number'

import { CidadaoGrupoItemModel, LotacaoGrupoItemModel } from './componentes/AtividadeColetivaGrupoSelectField'
import { ParticipanteEditableTableModel } from './componentes/participantes/ParticipanteEditableTable'
import { ACOES, TipoAcaoGrupo } from './grupos/model-atividadeColetivaGrupos'
import {
  AtividadeColetivaFilterFormModel,
  AtividadeColetivaReuniaoModel,
  AtividadeColetivaSaudeModel,
  AtividadePseModel,
  ParticipanteModel,
} from './model-atividadeColetiva'

type CidadaoNome = Pick<Cidadao, 'nome' | 'nomeSocial'>

export const getNomeCidadao = (model?: Partial<CidadaoNome>) => model?.nomeSocial ?? model?.nome

export const sortByNomeCidadao = (a: { cidadao: CidadaoNome }, b: { cidadao: CidadaoNome }) =>
  getNomeCidadao(a.cidadao).localeCompare(getNomeCidadao(b.cidadao))

const getNomeParticipante = (model: ParticipanteModel) =>
  getNomeCidadao(model.cidadao) ?? model.cidadaoParticipante.nome ?? 'ZZZ'

const getDataNascimentoParticipante = (model: ParticipanteModel) =>
  toDate(model.cidadao?.dataNascimento ?? model.cidadaoParticipante?.dataNascimento).getTime()

const compareDataNascimento = (a: ParticipanteModel, b: ParticipanteModel) => {
  const dataA = getDataNascimentoParticipante(a)
  const dataB = getDataNascimentoParticipante(b)
  return dataA < dataB ? 1 : dataA > dataB ? -1 : 0
}

export const sortByNomeAndIdadeParticipante = (a: ParticipanteModel, b: ParticipanteModel) =>
  10 * getNomeParticipante(a).localeCompare(getNomeParticipante(b)) + compareDataNascimento(a, b)

export const hasDataNascimentoAfterDataAtividade = (
  dataAtividade: LocalDate | Instant,
  dataNascimento: LocalDate | Instant
): boolean => !!dataNascimento && !!dataAtividade && toDate(dataNascimento) > toDate(dataAtividade)

export const isPse = (model: { atividadePse?: AtividadePseModel }): boolean =>
  model.atividadePse?.educacao || model.atividadePse?.saude

export const getLocalAtividade = (atividadeColetiva: AtividadeColetivaSaudeModel | AtividadeColetivaReuniaoModel) => {
  if (atividadeColetiva.inepLocalAtividade) {
    const { nomeEstabelecimento, dsInep } = atividadeColetiva.inepLocalAtividade
    return `INEP - ${nomeEstabelecimento ?? dsInep}`
  }

  if (atividadeColetiva.unidadeSaudeLocalAtividade) {
    const { cnes, nome } = atividadeColetiva.unidadeSaudeLocalAtividade
    return `CNES - ${nome ?? cnes}`
  }

  return atividadeColetiva.outraLocalidade
}

export const filterLotacoesFromGrupo = (
  lotacoesFromGrupo: ReadonlyArray<LotacaoGrupoItemModel>,
  currentLotacoesEnvolvidas: LotacaoEditableTableModel[]
): LotacaoGrupoItemModel[] => {
  const currentLotacoesIds = new Set(currentLotacoesEnvolvidas.map((lotacaoEnvolvida) => lotacaoEnvolvida.lotacao?.id))

  return lotacoesFromGrupo.filter((lotacao) => !currentLotacoesIds.has(lotacao.id))
}

export const filterCidadaosFromGrupo = (
  cidadaosFromGrupo: ReadonlyArray<CidadaoGrupoItemModel>,
  currentParticipantes: ParticipanteEditableTableModel[]
): CidadaoGrupoItemModel[] => {
  const currentCidadaosIds = new Set(currentParticipantes.map((participante) => participante.cidadao?.id))

  return cidadaosFromGrupo.filter((cidadao) => !currentCidadaosIds.has(cidadao.id))
}

export const removeLotacoesFromGrupo = (
  currentLotacoes: LotacaoEditableTableModel[],
  lotacoesFromGrupo: ReadonlyArray<LotacaoGrupoItemModel>
): LotacaoEditableTableModel[] => {
  const lotacoesFromGrupoIds = new Set(lotacoesFromGrupo.map((lotacao) => lotacao.id))
  return currentLotacoes.filter((lotacaoEnvolvida) => !lotacoesFromGrupoIds.has(lotacaoEnvolvida.lotacao?.id))
}

export const removeCidadaosFromGrupo = (
  currentParticipantes: ParticipanteEditableTableModel[],
  cidadaosFromGrupo: ReadonlyArray<CidadaoGrupoItemModel>
): ParticipanteEditableTableModel[] => {
  const cidadaosFromGrupoIds = new Set(cidadaosFromGrupo.map((cidadao) => cidadao.id))
  return currentParticipantes.filter((participante) => !cidadaosFromGrupoIds.has(participante.cidadao?.id))
}

const getMensagemGrupoBase = (
  tipoAcaoGrupo: TipoAcaoGrupo,
  nomeGrupo: string,
  qtdProfissionais: number,
  qtdParticipantes?: number
): string => {
  const { acao, singular, plural, preposicao } = ACOES[tipoAcaoGrupo]

  const itens: string[] = []

  if (qtdProfissionais > 0) {
    itens.push(qtdProfissionais === 1 ? '1 profissional' : `${qtdProfissionais} profissionais`)
  }

  if (qtdParticipantes > 0) {
    itens.push(qtdParticipantes === 1 ? '1 participante' : `${qtdParticipantes} participantes`)
  }

  const total = qtdProfissionais + (qtdParticipantes ?? 0)

  if (total === 0) {
    const alvo = qtdParticipantes === undefined ? 'nenhum profissional' : 'nenhum profissional ou participante'

    return `Vínculo com o grupo ${nomeGrupo} ${acao} com sucesso, ${alvo} foi ${singular} ${preposicao} atividade.`
  }

  const itensTexto = itens.join(' e ')
  const verbo = total === 1 ? `foi ${singular}` : `foram ${plural}`

  return `Vínculo com o grupo ${nomeGrupo} ${acao} com sucesso, ${itensTexto} ${verbo} ${preposicao} atividade.`
}

export const getMensagemGrupoAdicionado = (nomeGrupo: string, qtdProfissionais: number, qtdParticipantes?: number) =>
  getMensagemGrupoBase(TipoAcaoGrupo.ADICIONADO, nomeGrupo, qtdProfissionais, qtdParticipantes)

export const getMensagemGrupoRemovido = (nomeGrupo: string, qtdProfissionais: number, qtdParticipantes?: number) =>
  getMensagemGrupoBase(TipoAcaoGrupo.REMOVIDO, nomeGrupo, qtdProfissionais, qtdParticipantes)

export const isCleanFilter = (filter: AtividadeColetivaFilterFormModel): boolean =>
  !filter.equipes &&
  !filter.origens &&
  !filter.isProcessado &&
  !filter.tiposAtividade &&
  !filter.periodo?.startDate &&
  !filter.periodo?.endDate &&
  !filter.isSomenteRegistrosProprios &&
  !filter.nomeCnsCpfProfissionalResponsavel

export const getFiltroPadrao = (filter: AtividadeColetivaFilterFormModel): AtividadeColetivaFilterFormModel => ({
  isReuniao: filter.isReuniao,
  pageParams: filter.pageParams,
  fonteRegistros: filter.fonteRegistros,
})

export const getProgramaSaudeNaEscolaLabel = (
  atividadeEducacaoPse?: boolean,
  atividadeSaudePse?: boolean
): string | undefined =>
  (atividadeEducacaoPse && atividadeSaudePse && 'Educação, Saúde') ||
  (atividadeEducacaoPse && 'Educação') ||
  (atividadeSaudePse && 'Saúde') ||
  undefined

export const getCidadaoIdadeTooltipText = (
  hasDataNascimentoAfterDataAtividade: boolean,
  hasIdadeSuperiorIdadeMaxima: boolean
): string => {
  if (hasDataNascimentoAfterDataAtividade) {
    return 'A data de nascimento do cidadão foi atualizada e é posterior à data desta atividade'
  } else if (hasIdadeSuperiorIdadeMaxima) {
    return 'A data de nascimento do cidadão indica idade superior a 130 anos na data desta atividade'
  } else {
    return 'Idade no dia desta atividade'
  }
}

export const formatImcParticipante = (participante: ParticipanteModel): string | undefined => {
  if (!participante.peso || !participante.altura) return undefined

  return formatNumber(calcularIMC(participante.peso, participante.altura))
}
